// Shared building blocks for the two carded RESPONDENT form screens — the
// start-of-interview demographics (D3) and the post-interview Likert (L1).
//
// Every colour comes from an interviewTheme token object (`t`), so the same
// components render day or night with no per-screen branching. The visual spec is
// the approved `ai-interviewer-onepage-cards.html` mockup: an elevated
// frosted-glass card per question, a numbered badge, and either radio rows or a
// five-cell scale.
//
// A11y: choices are NATIVE inputs (visually hidden) inside their <label>, wrapped
// in a role="radiogroup"/"group" container. That buys correct semantics plus
// free arrow-key navigation within each group — no manual key handling.
import React from "react";
import { Box, Typography, Button, CircularProgress } from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import { useIntl } from "react-intl";

const NAVY = "#00438e";
const SKY = "#2ea3ff";
const AQUA = "#3ad2c6";
const TEAL = "#0e9c94";

export const SERIF = '"Lora", Georgia, serif';

// The header orb — the same conic aurora as InterviewOrb, but static and small
// (this is a welcome mark, not the live voice indicator, so it never animates).
export function HeaderOrb({ t, size = 56 }) {
    return (
        <Box
            aria-hidden="true"
            sx={{
                width: size,
                height: size,
                flexShrink: 0,
                borderRadius: "50%",
                background: `conic-gradient(from 0deg, ${NAVY}, ${SKY}, ${AQUA}, ${TEAL}, ${NAVY})`,
                boxShadow: t.headerOrbGlow,
            }}
        />
    );
}

// The OUTER elevated sheet the main content floats in, on the ambient glow.
//
// Page-level chrome — the top bar (brand/orb + title, progress, Pause/End) and the
// day/night ThemeSwitch — stays OUTSIDE this, rendered by each screen above the
// sheet. Only the content region goes in: the orb header, the question cards and
// the primary pill on the forms; the orb, caption and turn controls on the
// interview. Inner padding comes from the child stage, so each screen keeps the
// spacing it already had.
export function ContentSheet({ t, maxWidth = 780, children, sx }) {
    return (
        <Box
            sx={{
                flexGrow: 1,
                display: "flex",
                minHeight: 0,
                // Page gutter so the ambient glow reads around the sheet; near
                // full-bleed on narrow phones. Owned here so all three screens get
                // the same inset above and below the sheet.
                px: { xs: 1, sm: 3 },
                pt: { xs: 1, sm: 1.5 },
                pb: { xs: 1.5, sm: 3 },
            }}
        >
            <Box
                data-testid="content-sheet"
                sx={{
                    position: "relative",
                    flexGrow: 1,
                    width: "100%",
                    maxWidth,
                    mx: "auto",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: 0,
                    borderRadius: "22px",
                    overflow: "hidden",
                    background: t.sheetBg,
                    backdropFilter: t.sheetBlur,
                    WebkitBackdropFilter: t.sheetBlur,
                    border: `1px solid ${t.sheetBorder}`,
                    boxShadow: t.sheetShadow,
                    "@supports not ((backdrop-filter: blur(16px)) or (-webkit-backdrop-filter: blur(16px)))": {
                        background: t.sheetBgSolid,
                    },
                    ...sx,
                }}
            >
                {children}
            </Box>
        </Box>
    );
}

// The elevated question card. `danger` draws the unanswered-required ring.
export const formCardSx = (t, danger = false) => ({
    position: "relative",
    p: { xs: 2.25, sm: "20px 22px" },
    mb: 2,
    borderRadius: "16px",
    background: t.formCardBg,
    backdropFilter: t.formCardBlur,
    WebkitBackdropFilter: t.formCardBlur,
    border: danger ? `2px solid ${t.danger}` : `1px solid ${t.formCardBorder}`,
    boxShadow: t.formCardShadow,
    transition: "border-color .2s ease",
    // Without blur support the translucent fill would read as washed-out, so fall
    // back to a near-opaque card.
    "@supports not ((backdrop-filter: blur(10px)) or (-webkit-backdrop-filter: blur(10px)))": {
        background: t.formCardBgSolid,
    },
});

// Numbered badge (none when `n` is null) + the question text. `serif` renders a Likert statement (Lora);
// otherwise it's a demographic label (Inter 600).
//
// A required question carries a small asterisk after its label. Its COLOUR is live
// feedback — red while unanswered, neutral once answered — but the glyph itself is
// unconditional and a visually-hidden "required" rides along inside the labelled
// element, so nothing here depends on colour alone.
export function QuestionHead({ t, n, text, serif = false, id, required = false, answered = false }) {
    const intl = useIntl();
    return (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.4, mb: serif ? 2 : 1.9 }}>
            {n != null && <Box
                aria-hidden="true"
                sx={{
                    width: 24,
                    height: 24,
                    flexShrink: 0,
                    mt: "1px",
                    borderRadius: "50%",
                    display: "grid",
                    placeItems: "center",
                    backgroundColor: t.badgeBg,
                    color: t.badgeText,
                    fontSize: "0.78rem",
                    fontWeight: 600,
                }}
            >
                {n}
            </Box>}
            <Typography
                id={id}
                component="span"
                sx={
                    serif
                        ? { fontFamily: SERIF, fontWeight: 600, fontSize: { xs: "1.02rem", sm: "1.09rem" }, lineHeight: 1.32, color: t.formSerif }
                        : { fontWeight: 600, fontSize: "0.94rem", lineHeight: 1.35, color: t.formLabel }
                }
            >
                <Box
                    component="span"
                    // Question text is authored HTML from the assessment builder — the
                    // same treatment the pre-restyle form and the admin preview use.
                    // A wrapping <p> is inlined so the asterisk stays on the label's
                    // last line instead of being pushed below it.
                    sx={{ "& p": { display: "inline", m: 0 } }}
                    dangerouslySetInnerHTML={{ __html: text || "" }}
                />
                {required && (
                    <>
                        <Box
                            component="span"
                            aria-hidden="true"
                            sx={{
                                ml: "3px",
                                fontSize: "0.8em",
                                lineHeight: 1,
                                verticalAlign: "super",
                                color: answered ? t.formLabel : t.danger,
                                transition: "color .16s ease",
                                "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                            }}
                        >
                            *
                        </Box>
                        <Box component="span" sx={visuallyHidden}>
                            {intl.formatMessage({ id: "conversation.required_aria", defaultMessage: "required" })}
                        </Box>
                    </>
                )}
            </Typography>
        </Box>
    );
}

// One choice row — a radio (single choice) or a checkbox (multi choice).
export function ChoiceRow({ t, name, label, selected, onSelect, multi = false }) {
    return (
        <Box
            component="label"
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                px: 1.9,
                py: 1.5,
                borderRadius: "11px",
                cursor: "pointer",
                fontSize: "0.91rem",
                color: selected ? t.optSelText : t.optText,
                border: `1.5px solid ${selected ? t.optSelBorder : t.optBorder}`,
                background: selected ? t.optSelBg : t.optBg,
                transition: "border-color .16s ease, background-color .16s ease",
                "&:hover": { borderColor: t.optHoverBorder, background: selected ? t.optSelBg : t.optHoverBg },
                "&:has(input:focus-visible)": { borderColor: t.optHoverBorder, boxShadow: `0 0 0 3px ${t.focusRing}` },
                "@media (prefers-reduced-motion: reduce)": { transition: "none" },
            }}
        >
            <Box
                component="input"
                type={multi ? "checkbox" : "radio"}
                name={name}
                checked={selected}
                onChange={onSelect}
                sx={{
                    // Visually hidden but focusable, so the native input keeps its
                    // semantics and keyboard behaviour.
                    position: "absolute",
                    width: 1,
                    height: 1,
                    opacity: 0,
                    m: 0,
                    p: 0,
                    pointerEvents: "none",
                }}
            />
            <Box
                aria-hidden="true"
                sx={{
                    width: 19,
                    height: 19,
                    flexShrink: 0,
                    borderRadius: multi ? "5px" : "50%",
                    border: `2px solid ${selected ? t.radioFill : t.radioBorder}`,
                    background: selected
                        ? multi
                            ? t.radioFill
                            : `radial-gradient(circle, ${t.radioFill} 0 42%, transparent 46%)`
                        : "transparent",
                }}
            />
            <Box component="span" sx={{ lineHeight: 1.4 }}>
                {label}
            </Box>
        </Box>
    );
}

// The five-cell Likert scale. Each cell is the option's position number over its
// own scale label, and the whole row is one radiogroup for the statement.
export function ScaleRow({ t, name, options, selectedValue, onSelect, labelledBy, required = false }) {
    return (
        <Box
            role="radiogroup"
            aria-labelledby={labelledBy}
            aria-required={required || undefined}
            sx={{
                display: "flex",
                gap: 1,
                justifyContent: "center",
                // Narrow phones: let the cells wrap rather than overflow the card.
                flexWrap: { xs: "wrap", sm: "nowrap" },
            }}
        >
            {options.map((o, i) => {
                const selected = selectedValue != null && selectedValue === o.value;
                return (
                    <Box
                        key={o.key}
                        component="label"
                        sx={{
                            flex: { xs: "1 1 30%", sm: "1 1 0" },
                            minWidth: 0,
                            maxWidth: { sm: 100 },
                            minHeight: 44,
                            px: 0.75,
                            pt: 1.6,
                            pb: 1.4,
                            borderRadius: "12px",
                            textAlign: "center",
                            cursor: "pointer",
                            border: `1.5px solid ${selected ? t.scaleSelBorder : t.optBorder}`,
                            background: selected ? t.scaleSelBg : t.optBg,
                            transition: "border-color .16s ease, background-color .16s ease",
                            "&:hover": { borderColor: t.optHoverBorder, background: selected ? t.scaleSelBg : t.optHoverBg },
                            "&:has(input:focus-visible)": { borderColor: t.optHoverBorder, boxShadow: `0 0 0 3px ${t.focusRing}` },
                            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
                        }}
                    >
                        <Box
                            component="input"
                            type="radio"
                            name={name}
                            checked={selected}
                            onChange={() => onSelect(o.value)}
                            // The cell reads as its scale label, not "1Strongly agree":
                            // the digit is decorative, the label carries the meaning.
                            aria-label={o.label || String(i + 1)}
                            sx={{ position: "absolute", width: 1, height: 1, opacity: 0, m: 0, p: 0, pointerEvents: "none" }}
                        />
                        <Box aria-hidden="true" sx={{ fontSize: "1.18rem", fontWeight: 600, color: selected ? t.scaleSelText : t.scaleNum }}>
                            {i + 1}
                        </Box>
                        <Box
                            sx={{
                                mt: 0.6,
                                fontSize: "0.66rem",
                                lineHeight: 1.2,
                                minHeight: 24,
                                color: selected ? t.scaleSelText : t.scaleLabel,
                            }}
                        >
                            {o.label}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    );
}

// The Back pill beside it: the survey's own Back colours when the theme has them
// (`backBtnBg` / `backBtnText`), else an outlined pill.
export const backButtonSx = (t, base = {}) => ({
    textTransform: "none",
    borderRadius: "999px",
    px: 3,
    py: 1.2,
    fontWeight: 700,
    minWidth: 0,
    ...base,
    color: t.backBtnText || t.pauseText,
    backgroundColor: t.backBtnBg || t.pauseBg,
    border: `1px solid ${t.backBtnBg ? "transparent" : t.pauseBorder}`,
    "&:hover": { backgroundColor: t.backBtnBg || t.pauseBg, borderColor: t.backBtnBg ? "transparent" : t.pauseText, filter: "brightness(0.96)" },
    "&.Mui-disabled": { color: t.backBtnText || t.pauseText, opacity: 0.5 },
});

// The single primary pill each screen ends on. `disabled` greys it out (a
// required answer still missing); `busy` also shows a spinner.
export function PrimaryPill({ t, onClick, busy, disabled = false, endIcon, children }) {
    return (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 1, pb: 1 }}>
            <Button
                variant="contained"
                onClick={onClick}
                disabled={busy || disabled}
                startIcon={busy ? <CircularProgress size={16} sx={{ color: t.formBtnText || "#fff" }} /> : null}
                endIcon={busy ? null : endIcon}
                sx={{
                    backgroundColor: t.formBtnBg,
                    color: t.formBtnText || "#ffffff",
                    px: 4,
                    py: 1.4,
                    borderRadius: "999px",
                    textTransform: "none",
                    fontWeight: 600,
                    fontSize: "0.91rem",
                    boxShadow: t.formBtnShadow,
                    transition: "transform .15s ease, box-shadow .2s ease, background-color .2s ease",
                    "&:hover": { backgroundColor: t.formBtnHover, transform: "translateY(-1px)" },
                    "&.Mui-disabled": { backgroundColor: t.formBtnBg, color: t.formBtnText || "#ffffff", opacity: 0.65, boxShadow: "none" },
                    "@media (prefers-reduced-motion: reduce)": { transition: "none", "&:hover": { transform: "none" } },
                }}
            >
                {children}
            </Button>
        </Box>
    );
}

// The "Choose an option to continue" helper under an unanswered required card.
export function MissingHint({ t, children }) {
    return (
        <Typography sx={{ mt: 1.4, fontSize: "0.76rem", fontWeight: 500, color: t.danger }}>{children}</Typography>
    );
}

// Smooth-scroll the first unanswered card into view, honouring reduced motion.
export const scrollToCard = (node) => {
    if (!node || typeof node.scrollIntoView !== "function") return;
    let smooth = true;
    try {
        smooth = !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    } catch (e) {
        /* no-op */
    }
    node.scrollIntoView({ behavior: smooth ? "smooth" : "auto", block: "center" });
};
